import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './styles/main.css';

// Component imports
import Header from './components/Header';
import LoginPage from './components/LoginPage';
import UserDashboard from './components/UserDashboard';
import AdminDashboard from './components/admin/AdminDashboard';
import IKDashboard from './components/ik/IKDashboard';
import AdminInventory from './components/ik/AdminInventory';
import AdminUsers from './components/ik/AdminUsers';
import CreateFaultReport from './components/CreateFaultReport';
import CreateChangeRequest from './components/CreateChangeRequest';
import CreateGeneralRequest from './components/CreateGeneralRequest';
import MyTickets from './components/MyTickets';
import MyInventory from './components/MyInventory';
import InventoryCount from './components/InventoryCount';

// Constants
export const API_BASE = process.env.REACT_APP_API_URL || '/it/api';

// JWT payload'ını çözer. Geçersiz token'da hata fırlatır.
const decodeTokenPayload = (token) => {
  const part = token.split('.')[1];
  if (!part) throw new Error('Token formatı geçersiz');

  // JWT base64url kullanır: -/_ karakterlerini atob'un anladığı hale getir
  const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

  return JSON.parse(atob(padded));
};

// Utility functions
export const getFullNameByEmail = (email, users = []) => {
  const user = users.find(u => u.email === email);
  if (!user) return email;

  const first = user.first_name ?? user.firstName;
  const last = user.last_name ?? user.lastName;

  if (first && last) return `${first} ${last}`;
  return email;
};

// Ana App Component
function App() {
  const [currentPage, setCurrentPage] = useState('login');
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // 🔐 Otomatik logout fonksiyonu (birleştirilmiş hali)
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setCurrentPage('login');
  }, []);

  // Ana sayfa için fonksiyon
  const goToOverview = useCallback(() => {
    if (user) {
      if (user.role === 'admin') {
        setCurrentPage('admin-dashboard');
      } else if (user.role === 'ik') {
        setCurrentPage('ik-dashboard');
      } else {
        setCurrentPage('user-dashboard');
      }
    }
  }, [user]);

  // ⏱️ Pasiflik kontrolü
  const timerRef = useRef(null);
  const resetTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      logout(); // kullanıcı 15dk pasifse çıkış
    }, 15 * 60 * 1000); // 15 dakika
  }, [logout]);

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    events.forEach(event => window.addEventListener(event, resetTimer));
    resetTimer(); // ilk başlatma

    return () => {
      events.forEach(event => window.removeEventListener(event, resetTimer));
      clearTimeout(timerRef.current);
    };
  }, [resetTimer]);

  // ⚠️ Token süresi dolmuşsa backend 401 döner → logout
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [logout]);
  
  // ⚠️ Bu useEffect'i kaldırın veya yoruma alın
  /*
  useEffect(() => {
    const handleBeforeUnload = () => {
      logout();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [logout]);
  */

  // ✅ Token varsa kullanıcıyı ayarla
  useEffect(() => {
    if (token) {
      try {
        const payload = decodeTokenPayload(token);

        // Süresi dolmuş veya exp taşımayan token ile oturum açılmaz
        if (!payload.exp || payload.exp * 1000 <= Date.now()) {
          throw new Error('Token süresi dolmuş');
        }

        // Token'dan gelen verileri düzenleyerek user objesini oluştur
        const userFromToken = {
          ...payload,
          // full_name yoksa oluştur
          full_name: payload.full_name || 
                    (payload.first_name && payload.last_name ? 
                     `${payload.first_name} ${payload.last_name}` : 
                     (payload.first_name || payload.last_name || ''))
        };
        
        // Debug: Oluşturulan user objesini konsola yazdır
        console.log('App.js\'den set edilen user:', userFromToken);
        
        setUser(userFromToken);
        if (payload.role === 'admin') {
          setCurrentPage('admin-dashboard');
        } else if (payload.role === 'ik') {
          setCurrentPage('ik-dashboard');
        } else {
          setCurrentPage('user-dashboard');
        }
      } catch (error) {
        console.warn('Geçersiz oturum, giriş ekranına dönülüyor:', error.message);
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setCurrentPage('login');
      }
    }
  }, [token]);

  // ✅ Uygulama arayüzü
  return (
    <div className="App">
      <Header 
        user={user} 
        onLogout={logout} 
        setActiveTab={goToOverview}
      />
      
      {currentPage === 'login' && (
        <LoginPage 
          onLogin={(token, user) => {
            setToken(token);
            setUser(user);
            localStorage.setItem('token', token);
            if (user.role === 'admin') {
              setCurrentPage('admin-dashboard');
            } else if (user.role === 'ik') {
              setCurrentPage('ik-dashboard');
            } else {
              setCurrentPage('user-dashboard');
            }
          }}
        />
      )}
      
      {currentPage === 'user-dashboard' && user && (
        <UserDashboard 
          user={user} 
          token={token}
          onNavigate={setCurrentPage}
        />
      )}
      
      {currentPage === 'admin-dashboard' && user && user.role === 'admin' && (
        <AdminDashboard 
          user={user} 
          token={token}
          onNavigate={setCurrentPage}
        />
      )}
      
      {currentPage === 'ik-dashboard' && user && user.role === 'ik' && (
        <IKDashboard 
          user={user} 
          token={token}
          onNavigate={setCurrentPage}
        />
      )}
      
      {currentPage === 'ik-inventory' && user && user.role === 'ik' && (
        <AdminInventory 
          token={token}
          onBack={() => setCurrentPage('ik-dashboard')}
        />
      )}
      
      {currentPage === 'ik-users' && user && user.role === 'ik' && (
        <AdminUsers 
          token={token}
          onBack={() => setCurrentPage('ik-dashboard')}
        />
      )}
      
      {currentPage === 'create-fault-report' && user && (
        <CreateFaultReport 
          user={user} 
          token={token}
          onBack={() => {
            if (user.role === 'ik') {
              setCurrentPage('ik-dashboard');
            } else {
              setCurrentPage('user-dashboard');
            }
          }}
        />
      )}
      
      {currentPage === 'create-change-request' && user && (
        <CreateChangeRequest 
          user={user} 
          token={token}
          onBack={() => {
            if (user.role === 'ik') {
              setCurrentPage('ik-dashboard');
            } else {
              setCurrentPage('user-dashboard');
            }
          }}
        />
      )}
      
      {currentPage === 'create-general-request' && user && (
        <CreateGeneralRequest 
          user={user} 
          token={token}
          onBack={() => {
            if (user.role === 'ik') {
              setCurrentPage('ik-dashboard');
            } else {
              setCurrentPage('user-dashboard');
            }
          }}
        />
      )}
      
      {currentPage === 'my-tickets' && user && (
        <MyTickets 
          user={user} 
          token={token}
          onBack={() => {
            if (user.role === 'ik') {
              setCurrentPage('ik-dashboard');
            } else {
              setCurrentPage('user-dashboard');
            }
          }}
        />
      )}

      {currentPage === 'my-inventory' && user && (
        <MyInventory 
          user={user} 
          token={token}
          onBack={() => {
            if (user.role === 'ik') {
              setCurrentPage('ik-dashboard');
            } else {
              setCurrentPage('user-dashboard');
            }
          }}
          onNavigate={setCurrentPage}
        />
      )}

      {currentPage === 'inventory-count' && user && (
        <InventoryCount 
          user={user} 
          token={token}
          onBack={() => {
            if (user.role === 'ik') {
              setCurrentPage('ik-dashboard');
            } else {
              setCurrentPage('user-dashboard');
            }
          }}
        />
      )}
    </div>
  );
}

export default App;